/**
 * Accès de la clientèle : des groupes (« Campeurs 2026 », « Membres du club »…) qui voient le contenu privé de
 * l'organisation (chemins et équipements marqués « Privé »). On y entre parce qu'un gestionnaire ajoute le compte,
 * ou en scannant un code QR (journée, semaine, mois, saison) : l'accès est accordé tout de suite.
 * Plan : docs/todo/gestion-sections-a-venir.md, section 2.
 */
import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { QrCode } from '@web/shared/components/QrCode'
import {
  addClientAccessMember,
  createClientAccessCode,
  deleteClientAccessGroup,
  fetchClientAccess,
  removeClientAccessMember,
  saveClientAccessGroup,
  setClientAccessCodeActive,
  type ClientAccess,
  type ClientAccessCode,
  type ClientAccessGroup,
} from '../api'
import { formatDate } from '../utils/format'
import { publicSiteUrl } from '../utils/links'
import { useOrg } from '../utils/orgContext'

const DURATIONS = ['day', 'week', 'month', 'season'] as const

const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e))
export const redeemUrl = (code: string) => publicSiteUrl(`/acces/${code}`)

export function AccesClientelePage() {
  const { t } = useTranslation('gestion')
  const { orgId, orgName } = useOrg()
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useQuery({ queryKey: ['clientAccess', orgId], queryFn: ({ signal }) => fetchClientAccess(orgId, signal) })
  const update = (next: ClientAccess) => queryClient.setQueryData(['clientAccess', orgId], next)
  const [newName, setNewName] = useState('')
  const [busy, setBusy] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const create = async () => {
    setBusy(true)
    setCreateError(null)
    try {
      update(await saveClientAccessGroup(orgId, null, { name: newName.trim() }))
      setNewName('')
    } catch (e) {
      setCreateError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="g-page">
      <div className="g-page__head">
        <div>
          <h1 className="g-page__title">{t('acces.titre')}</h1>
          <p className="g-muted">{t('acces.intro')}</p>
        </div>
      </div>
      <p className="g-hint">{t('acces.ce_qui_est_prive')}</p>
      {error && <p className="g-alert g-alert--error">{(error as Error).message}</p>}

      <section className="g-card">
        <div className="g-card__head">
          <h2 className="g-card__title">{t('acces.nouveau_groupe')}</h2>
        </div>
        <div className="g-acces__row">
          <input
            className="g-input"
            value={newName}
            placeholder={t('acces.nom_exemple')}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim()) void create()
            }}
          />
          <button type="button" className="btn btn--primary" disabled={busy || !newName.trim()} onClick={() => void create()}>
            <span className="mdi mdi-plus" aria-hidden /> {t('acces.creer_groupe')}
          </button>
        </div>
        {createError && <p className="g-alert g-alert--error">{createError}</p>}
      </section>

      {isLoading || !data ? (
        <p className="g-muted">{t('commun.chargement')}</p>
      ) : data.groups.length === 0 ? (
        <p className="g-muted">{t('acces.aucun_groupe')}</p>
      ) : (
        data.groups.map((g) => <GroupCard key={g.id} orgId={orgId} orgName={orgName} group={g} onSaved={update} />)
      )}
    </div>
  )
}

function GroupCard({ orgId, orgName, group, onSaved }: { orgId: number; orgName: string; group: ClientAccessGroup; onSaved: (next: ClientAccess) => void }) {
  const { t } = useTranslation('gestion')
  const [name, setName] = useState(group.name)
  const [description, setDescription] = useState(group.description)
  const [endsOn, setEndsOn] = useState(group.ends_on ?? '')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const dirty = name !== group.name || description !== group.description || endsOn !== (group.ends_on ?? '')

  const run = async (fn: () => Promise<ClientAccess>) => {
    setBusy(true)
    setError(null)
    try {
      onSaved(await fn())
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }
  const remove = () => {
    if (!window.confirm(t('acces.supprimer_groupe', { name: group.name }))) return
    void run(() => deleteClientAccessGroup(orgId, group.id))
  }

  return (
    <section className="g-card g-acces">
      <div className="g-card__head">
        <h2 className="g-card__title">
          {group.name} {!group.is_active && <span className="g-chip g-chip--sm">{t('acces.inactif')}</span>}
        </h2>
        <span className="g-muted">{t('acces.n_membres', { count: group.members.length })}</span>
      </div>

      <div className="g-acces__fields">
        <label className="g-field">
          <span className="g-field__label">{t('acces.nom')}</span>
          <input className="g-input" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="g-field">
          <span className="g-field__label">{t('acces.fin_groupe')}</span>
          <input className="g-input" type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
        </label>
        <label className="g-field g-acces__wide">
          <span className="g-field__label">{t('acces.description')}</span>
          <input className="g-input" value={description} placeholder={t('acces.description_exemple')} onChange={(e) => setDescription(e.target.value)} />
        </label>
      </div>
      <div className="g-editor__buttons">
        <button type="button" className="btn btn--primary" disabled={busy || !dirty || !name.trim()} onClick={() => void run(() => saveClientAccessGroup(orgId, group.id, { name, description, ends_on: endsOn || null }))}>
          {busy ? t('commun.enregistrement') : t('commun.enregistrer')}
        </button>
        <label className="g-check">
          <input type="checkbox" checked={group.is_active} disabled={busy} onChange={(e) => void run(() => saveClientAccessGroup(orgId, group.id, { is_active: e.target.checked }))} />
          <span>{t('acces.actif')}</span>
        </label>
        <span className="g-spacer" />
        <button type="button" className="btn g-danger" disabled={busy} onClick={remove}>
          {t('acces.supprimer')}
        </button>
      </div>
      {error && <p className="g-alert g-alert--error">{error}</p>}

      <CodesBlock orgId={orgId} orgName={orgName} group={group} run={run} busy={busy} />
      <MembersBlock orgId={orgId} group={group} run={run} busy={busy} />
    </section>
  )
}

type BlockProps = { orgId: number; group: ClientAccessGroup; run: (fn: () => Promise<ClientAccess>) => Promise<void>; busy: boolean }

function CodesBlock({ orgId, orgName, group, run, busy }: BlockProps & { orgName: string }) {
  const { t } = useTranslation('gestion')
  const [duration, setDuration] = useState<string>('day')
  const [maxUses, setMaxUses] = useState('')
  const [expires, setExpires] = useState('')
  const codes = group.codes

  const create = () =>
    run(async () => {
      const next = await createClientAccessCode(orgId, group.id, { duration, max_uses: maxUses.trim() ? Number(maxUses) : null, expires_at: expires || null })
      setMaxUses('')
      setExpires('')
      return next
    })

  return (
    <div className="g-acces__block">
      <h3 className="g-editor__section">{t('acces.codes_titre')}</h3>
      <p className="g-muted">{t('acces.codes_aide')}</p>
      <div className="g-acces__row">
        <label className="g-field">
          <span className="g-field__label">{t('acces.duree')}</span>
          <select className="g-input" value={duration} onChange={(e) => setDuration(e.target.value)}>
            {DURATIONS.map((d) => (
              <option key={d} value={d}>
                {t(`acces.duree_${d}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="g-field">
          <span className="g-field__label">{t('acces.utilisations_max')}</span>
          <input className="g-input g-acces__num" inputMode="numeric" value={maxUses} placeholder={t('acces.illimite')} onChange={(e) => setMaxUses(e.target.value)} />
        </label>
        <label className="g-field">
          <span className="g-field__label">{t('acces.code_valide_jusqu')}</span>
          <input className="g-input" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
        </label>
        <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void create()}>
          <span className="mdi mdi-qrcode-plus" aria-hidden /> {t('acces.creer_code')}
        </button>
      </div>
      {codes.length === 0 ? (
        <p className="g-hint">{t('acces.aucun_code')}</p>
      ) : (
        <div className="g-acces__codes">
          {codes.map((c) => (
            <CodeCard key={c.id} orgId={orgId} orgName={orgName} group={group} code={c} run={run} busy={busy} />
          ))}
        </div>
      )}
    </div>
  )
}

function CodeCard({ orgId, orgName, group, code, run, busy }: BlockProps & { orgName: string; code: ClientAccessCode }) {
  const { t } = useTranslation('gestion')
  const qrRef = useRef<HTMLDivElement>(null)
  const [copied, setCopied] = useState(false)
  const url = redeemUrl(code.code)
  const usable = code.problem == null

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      window.prompt(t('acces.copier_lien'), url)
    }
  }

  /** Affiche à imprimer : nom de l'organisation et du groupe, QR, lien et code à saisir. */
  const print = () => {
    const img = qrRef.current?.querySelector('img')?.getAttribute('src')
    const w = window.open('', '_blank', 'width=720,height=900')
    if (!img || !w) return
    const esc = (s: string) => s.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch] as string)
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(group.name)}</title>
<style>body{font-family:system-ui,sans-serif;text-align:center;padding:2.5rem;color:#222}h1{margin:0 0 .25rem;font-size:2rem}
h2{margin:0 0 1.5rem;font-weight:500;color:#555}img{width:340px;height:340px}p{font-size:1.15rem;margin:.6rem 0}
code{font-size:1.6rem;letter-spacing:.15em;font-weight:700}</style></head><body>
<h1>${esc(orgName)}</h1><h2>${esc(group.name)}</h2><img src="${img}" alt=""><p>${esc(t('acces.affiche_scanner'))}</p>
<p>${esc(t('acces.affiche_ou_code'))}<br><code>${esc(code.code)}</code></p><p style="color:#666">${esc(url)}</p>
<script>window.onload=function(){window.print()}</script></body></html>`)
    w.document.close()
  }

  return (
    <div className={`g-acces__code${usable ? '' : ' g-acces__code--off'}`}>
      <div ref={qrRef}>
        <QrCode value={url} size={120} />
      </div>
      <div className="g-acces__code-info">
        <code className="g-acces__code-text">{code.code}</code>
        <span>
          {t(`acces.duree_${code.duration}`)} · {code.max_uses == null ? t('acces.n_utilisations', { count: code.uses }) : t('acces.utilisations_sur', { n: code.uses, max: code.max_uses })}
        </span>
        {code.expires_at && <span className="g-muted">{t('acces.valide_jusqu', { date: formatDate(code.expires_at) })}</span>}
        {!usable && <span className="g-acces__warn">{t(`acces.probleme_${code.problem}`)}</span>}
        <div className="g-acces__row">
          <button type="button" className="btn" onClick={print} disabled={!usable}>
            <span className="mdi mdi-printer-outline" aria-hidden /> {t('acces.imprimer')}
          </button>
          <button type="button" className="btn" onClick={() => void copy()}>
            <span className="mdi mdi-link-variant" aria-hidden /> {copied ? t('acces.copie') : t('acces.copier_lien')}
          </button>
          <button type="button" className="g-link" disabled={busy} onClick={() => void run(() => setClientAccessCodeActive(orgId, group.id, code.id, !code.is_active))}>
            {code.is_active ? t('acces.desactiver') : t('acces.reactiver')}
          </button>
        </div>
      </div>
    </div>
  )
}

function MembersBlock({ orgId, group, run, busy }: BlockProps) {
  const { t } = useTranslation('gestion')
  const [identifier, setIdentifier] = useState('')
  const [expires, setExpires] = useState('')

  const add = () =>
    run(async () => {
      const next = await addClientAccessMember(orgId, group.id, identifier.trim(), expires || null)
      setIdentifier('')
      setExpires('')
      return next
    })
  const remove = (grantId: number, label: string) => {
    if (!window.confirm(t('acces.retirer_membre', { name: label }))) return
    void run(() => removeClientAccessMember(orgId, group.id, grantId))
  }

  return (
    <div className="g-acces__block">
      <h3 className="g-editor__section">{t('acces.membres_titre')}</h3>
      <div className="g-acces__row">
        <label className="g-field g-acces__wide">
          <span className="g-field__label">{t('acces.ajouter_compte')}</span>
          <input className="g-input" value={identifier} placeholder={t('acces.identifiant_exemple')} onChange={(e) => setIdentifier(e.target.value)} />
        </label>
        <label className="g-field">
          <span className="g-field__label">{t('acces.acces_jusqu')}</span>
          <input className="g-input" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
        </label>
        <button type="button" className="btn btn--primary" disabled={busy || !identifier.trim()} onClick={() => void add()}>
          <span className="mdi mdi-account-plus-outline" aria-hidden /> {t('acces.ajouter')}
        </button>
      </div>
      <p className="g-hint">{t('acces.ajouter_aide')}</p>
      {group.members.length > 0 && (
        <div className="g-table-wrap">
          <table className="g-table">
            <thead>
              <tr>
                <th>{t('acces.compte')}</th>
                <th>{t('acces.par')}</th>
                <th>{t('acces.depuis')}</th>
                <th>{t('acces.jusqu')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {group.members.map((m) => {
                const label = m.user.name || m.user.username
                return (
                  <tr key={m.id} className={m.current ? undefined : 'g-acces__expired'}>
                    <td>
                      <strong>{label}</strong> <span className="g-muted">@{m.user.username}</span>
                    </td>
                    <td>{m.via_code ? t('acces.via_code', { code: m.via_code }) : t('acces.via_gestionnaire', { name: m.granted_by ?? '—' })}</td>
                    <td>{formatDate(m.created_at)}</td>
                    <td>{m.expires_at ? formatDate(m.expires_at) : t('acces.sans_fin')}{!m.current && ` (${t('acces.expire')})`}</td>
                    <td className="g-table__actions">
                      <button type="button" className="g-link g-danger" disabled={busy} onClick={() => remove(m.id, label)}>
                        {t('acces.retirer')}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
